import { useState } from "react";
import {
  Dialog,
  Flex,
  Text,
  TextField,
  TextArea,
  Button,
  Switch,
  Box,
} from "@radix-ui/themes";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "@tanstack/react-router";
import { useCreateBoard } from "@/hooks/use-api";
import { FieldError } from "@/components/ui/field-error";

const createBoardSchema = z.object({
  name: z.string().min(2, "Nom requis (min 2 caractères)").max(60),
  slug: z
    .string()
    .min(2, "Slug requis (min 2 caractères)")
    .max(30)
    .regex(/^[a-z0-9-]+$/, "Uniquement des lettres minuscules, chiffres et tirets"),
  description: z.string().max(200).optional(),
  public: z.boolean().default(false),
});

type CreateBoardInput = z.infer<typeof createBoardSchema>;

interface CreateBoardDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function CreateBoardDialog({ open, onOpenChange }: CreateBoardDialogProps) {
  const navigate = useNavigate();
  const createBoard = useCreateBoard();

  const {
    register,
    handleSubmit,
    control,
    watch,
    setValue,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<CreateBoardInput>({
    resolver: zodResolver(createBoardSchema),
    defaultValues: { public: false },
  });

  // Auto-générer le slug depuis le nom
  function handleNameChange(value: string) {
    const slug = value
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .replace(/[^a-z0-9\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-")
      .slice(0, 30);
    setValue("slug", slug, { shouldValidate: false });
  }

  async function onSubmit(data: CreateBoardInput) {
    const result = await createBoard.mutateAsync(data);
    if (!result.ok) return; // toast géré dans le hook
    reset();
    onOpenChange(false);
    navigate({ to: "/boards/$slug", params: { slug: data.slug } });
  }

  return (
    <Dialog.Root
      open={open}
      onOpenChange={(o) => {
        if (!o) reset();
        onOpenChange(o);
      }}
    >
      <Dialog.Content style={{ maxWidth: "480px" }}>
        <Dialog.Title>Créer un board</Dialog.Title>
        <Dialog.Description size="2" color="gray" mb="4">
          Un board est un espace partagé où vous pouvez poster des photos et des messages.
        </Dialog.Description>

        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <Flex direction="column" gap="4">
            {/* Nom */}
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium" htmlFor="board-name">
                Nom du board
              </Text>
              <TextField.Root
                id="board-name"
                placeholder="La famille Dupont"
                size="3"
                {...register("name", {
                  onChange: (e) => handleNameChange(e.target.value),
                })}
                color={errors.name ? "red" : undefined}
              />
              <FieldError message={errors.name?.message} />
            </Flex>

            {/* Slug */}
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium" htmlFor="board-slug">
                Identifiant (URL)
              </Text>
              <TextField.Root
                id="board-slug"
                placeholder="la-famille-dupont"
                size="3"
                {...register("slug")}
                color={errors.slug ? "red" : undefined}
              />
              <Text size="1" color="gray">
                mem.pm/<strong>{watch("slug") || "…"}</strong>
              </Text>
              <FieldError message={errors.slug?.message} />
            </Flex>

            {/* Description */}
            <Flex direction="column" gap="1">
              <Text as="label" size="2" weight="medium" htmlFor="board-desc">
                Description{" "}
                <Text size="1" color="gray">
                  (optionnel)
                </Text>
              </Text>
              <TextArea
                id="board-desc"
                placeholder="Un espace pour partager les moments en famille…"
                rows={3}
                {...register("description")}
              />
              <FieldError message={errors.description?.message} />
            </Flex>

            {/* Public */}
            <Flex align="center" justify="between">
              <Box>
                <Text size="2" weight="medium" as="p">
                  Board public
                </Text>
                <Text size="1" color="gray" as="p">
                  Visible sans connexion via le lien display
                </Text>
              </Box>
              <Controller
                name="public"
                control={control}
                render={({ field }) => (
                  <Switch
                    checked={field.value}
                    onCheckedChange={field.onChange}
                  />
                )}
              />
            </Flex>

            {/* Actions */}
            <Flex gap="3" justify="end" mt="2">
              <Dialog.Close>
                <Button variant="soft" color="gray" type="button">
                  Annuler
                </Button>
              </Dialog.Close>
              <Button type="submit" disabled={isSubmitting}>
                {isSubmitting ? "Création…" : "Créer le board"}
              </Button>
            </Flex>
          </Flex>
        </form>
      </Dialog.Content>
    </Dialog.Root>
  );
}
